import React, { useEffect, useRef, useState } from "react";
import { Mail, MessageSquare, MessageCircle, Search, Info } from "lucide-react";
import { staffApi } from "@/lib/staffApi";
import { Skeleton, ErrorState } from "@/components/BookingShell";
import { CHANNEL_LABEL, TRIGGER_LABEL, formatDateTime, friendlyStatus, statusIcon } from "./shared";

const CHANNEL_ICON = { email: Mail, sms: MessageSquare, whatsapp: MessageCircle };

// Group raw statuses into 4 friendly buckets for the History filter.
const STATUS_GROUPS = {
    all:       null,
    scheduled: ["scheduled", "pending"],
    sent:      ["sent"],
    failed:    ["failed"],
    cancelled: ["cancelled", "skipped"],
};

export function CommsHistory() {
    const [items, setItems] = useState(null);
    const [error, setError] = useState(null);
    const [group, setGroup] = useState("all");
    const [channel, setChannel] = useState("all");
    const [q, setQ] = useState("");
    const [expanded, setExpanded] = useState(null);
    const [offset,setOffset]=useState(0), [total,setTotal]=useState(0);
    const request=useRef(0);

    const load = () => {
        const current=++request.current;
        setError(null);
        staffApi.commsListQueue({limit:50, offset, newest:true, search:q || undefined,
            statuses:STATUS_GROUPS[group]?.join(','), channel:channel === 'all' ? undefined : channel})
            .then(r=>{if(current===request.current){setItems(r.items || []);setTotal(r.total || 0);}})
            .catch(e=>{if(current===request.current)setError(e);});
    };
    useEffect(load, [offset,group,channel,q]);

    const filtered = items || [];

    if (error) return <ErrorState error={error} onRetry={load} />;

    return (
        <div className="space-y-4" data-testid="comms-history-page">
            <div>
                <h2 className="font-serif text-2xl text-white">Message history</h2>
                <p className="text-sm text-white/50 mt-1">Every message we've queued, sent, or held back.</p>
                <button onClick={load} className="text-gold">Refresh</button>
            </div>

            <div className="rounded-2xl border hairline p-3 bg-white/[0.02] space-y-3">
                <div className="flex gap-2 items-center min-w-0">
                    <Search className="h-4 w-4 text-white/40 shrink-0" />
                    <input value={q} onChange={(e) => {setOffset(0);setQ(e.target.value);}} placeholder="Search by client name, email, phone or template"
                           data-testid="history-search"
                           className="flex-1 min-w-0 bg-transparent text-sm text-white placeholder-white/30 focus:outline-none" />
                </div>
                <div className="flex gap-1.5 flex-wrap">
                    {[
                        ["all", "All"],
                        ["scheduled", "Scheduled"],
                        ["sent", "Sent"],
                        ["failed", "Failed"],
                        ["cancelled", "Cancelled"],
                    ].map(([k, l]) => (
                        <button key={k} onClick={() => {setOffset(0); setGroup(k);}}
                                data-testid={`history-group-${k}`}
                                className={`h-8 px-3 rounded-full text-xs border ${group === k ? "bg-gold text-navy border-gold" : "hairline text-white/70 hover:text-gold hover:border-gold/40"}`}>
                            {l}
                        </button>
                    ))}
                    <select value={channel} onChange={(e) => {setOffset(0);setChannel(e.target.value);}}
                            data-testid="history-channel"
                            className="h-8 rounded-full bg-transparent border hairline text-xs text-white/80 px-3 ml-auto">
                        <option value="all">All channels</option>
                        {["email", "sms", "whatsapp"].map((c) => <option key={c} value={c}>{CHANNEL_LABEL[c]}</option>)}
                    </select>
                </div>
            </div>

            {!items ? <Skeleton className="h-40" /> : filtered.length === 0 ? (
                <div className="rounded-2xl border hairline p-8 text-center" data-testid="history-empty">
                    <Info className="h-6 w-6 text-white/30 mx-auto mb-2" strokeWidth={1.5} />
                    <p className="text-white/60 text-sm">No messages match your filter yet.</p>
                    <p className="text-[11px] text-white/40 mt-1">Switch on an automation, or send a manual message to see it here.</p>
                </div>
            ) : (
                <ul className="space-y-2" data-testid="history-list">
                    {filtered.map((it) => {
                        const Icon = CHANNEL_ICON[it.channel] || Mail;
                        const isOpen = expanded === it.id;
                        return (
                            <li key={it.id} className="rounded-2xl border hairline" data-testid={`history-row-${it.id}`}>
                                <button onClick={() => setExpanded(isOpen ? null : it.id)} className="w-full text-left p-4 flex items-center gap-3">
                                    <div className="h-10 w-10 rounded-full glass flex items-center justify-center shrink-0">
                                        <Icon className="h-4 w-4 text-gold" strokeWidth={1.5} />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="text-white font-ui text-sm break-words">{it.recipient?.name || "Client"} — {it.template_name || "Message"}</div>
                                        <div className="text-[11px] text-white/50 flex flex-wrap gap-2 mt-0.5">
                                            <span>{TRIGGER_LABEL[it.trigger] || it.trigger}</span>
                                            <span>·</span>
                                            <span>{formatDateTime(it.sent_at || it.scheduled_at || it.created_at)}</span>
                                            {it.recipient?.email && <><span>·</span><span className="text-white/70">{it.recipient.email}</span></>}
                                            {it.recipient?.phone && <><span>·</span><span className="text-white/70">{it.recipient.phone}</span></>}
                                        </div>
                                    </div>
                                    <span className="text-[11px] text-white/85 inline-flex items-center gap-1 shrink-0">
                                        {statusIcon(it.status)} {friendlyStatus(it.status)}
                                    </span>
                                </button>
                                {isOpen && (
                                    <div className="border-t hairline p-4 space-y-3" data-testid={`history-expand-${it.id}`}>
                                        {it.last_error && <p role="status" className="text-amber-200">{it.last_error}</p>}
                                        {it.rendered?.subject && (
                                            <div>
                                                <div className="text-[10px] uppercase tracking-[0.16em] text-white/45">Subject</div>
                                                <div className="text-white text-sm">{it.rendered.subject}</div>
                                            </div>
                                        )}
                                        <div>
                                            <div className="text-[10px] uppercase tracking-[0.16em] text-white/45">Message</div>
                                            <div className="text-white text-sm whitespace-pre-wrap bg-white/[0.02] rounded-lg border hairline p-3 max-h-56 overflow-y-auto">
                                                {it.rendered?.body || <span className="text-white/60">The message body was not saved for this older message.</span>}
                                                {it.content_missing && Object.entries(it.template_parameters || {}).map(([key,value])=><p key={key} className="break-all"><strong>{key.replaceAll('_',' ')}: </strong>{String(value)}</p>)}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}
            <div className="flex gap-4">
                {offset>0 && <button onClick={()=>setOffset(Math.max(0,offset-50))}>Newer messages</button>}
                {offset+50<total && <button onClick={()=>setOffset(offset+50)}>Older messages</button>}
            </div>
        </div>
    );
}
